// Dedicated trade × city hubs. Case studies reference real entries in
// caseStudiesData.ts by slug — never invent results here.

export type PlanPhase = { when: string; title: string; steps: string[] };

export type TradeCityHub = {
  path: string;
  trade: string;
  city: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  areas: string[];
  searches: string[];
  plan: PlanPhase[];
  caseStudySlugs: string[];
  caseStudyNote?: string;
  related: { label: string; to: string }[];
  faqs: { q: string; a: string }[];
};

const reviews = "Ask every finished job for a review with a direct link, and reply to each one.";

export const TRADE_CITY_HUBS: TradeCityHub[] = [
  {
    path: "/denver-roofing-marketing",
    trade: "Roofing",
    city: "Denver",
    metaTitle: "Denver Roofing Marketing | SEO, Google Maps & Ads",
    metaDescription: "Local SEO plan, Google Maps, and storm-season ads for Denver roofers, plus a real Denver roofing case study.",
    intro: "Denver is one of the most crowded roofing markets in the country, so we focus on the neighborhoods and suburbs where you can win, plus storm-season searches the big brands miss.",
    areas: ["Lakewood", "Aurora", "Englewood", "Wheat Ridge", "Arvada", "Centennial"],
    searches: ["hail damage roof repair Denver", "roof replacement Denver", "roof insurance claim help", "roofer near me"],
    plan: [
      { when: "Days 1–30", title: "Foundation", steps: ["Fix your Google Business Profile: main category, service areas, real job photos.", "Clean up your business listings so name, address, and phone match everywhere.", "Make sure your website loads fast on phones with a tap-to-call button."] },
      { when: "Days 31–60", title: "Pages that match searches", steps: ["Hail damage, roof replacement, and insurance claim pages with local photos.", "Neighborhood pages only for areas you really want more work in.", reviews] },
      { when: "Days 61–90", title: "Storm readiness", steps: ["Ads ready to turn up after a hailstorm and down when you're booked.", "Track calls by source so you know what's working.", "Monthly review of what moved and what to fix next."] },
    ],
    caseStudySlugs: ["roofing-denver", "roofing-falcon"],
    related: [
      { label: "Roofing SEO in Denver", to: "/roofing-seo-denver" },
      { label: "Roofing web design in Denver", to: "/roofing-web-design-denver" },
      { label: "Roofing marketing in Colorado", to: "/roofing-marketing-colorado" },
      { label: "Roofing leads guide", to: "/learn/roofing-leads-colorado" },
    ],
    faqs: [
      { q: "Can a smaller roofer compete in Denver?", a: "Yes, by focusing on specific suburbs and storm searches instead of broad terms the national brands spend heavily on." },
      { q: "How long does roofing SEO take in Denver?", a: "Google profile changes often show in a few months; stronger rankings in a crowded market usually take longer. We don't promise rankings." },
    ],
  },
  {
    path: "/colorado-springs-hvac-marketing",
    trade: "HVAC",
    city: "Colorado Springs",
    metaTitle: "Colorado Springs HVAC Marketing | Local SEO Plan",
    metaDescription: "Google Maps, seasonal service pages, and a 90-day local SEO plan for Colorado Springs HVAC companies, with a real local case study.",
    intro: "Our home market. We help Springs HVAC companies stay visible in both the first cold snap and the July heat — across Briargate, Powers, Monument, Falcon, and Fountain.",
    areas: ["Briargate", "Powers", "Monument", "Falcon", "Fountain", "Security-Widefield"],
    searches: ["furnace repair Colorado Springs", "AC repair near me", "heat pump installation", "HVAC company Colorado Springs"],
    plan: [
      { when: "Days 1–30", title: "Foundation", steps: ["Google Business Profile: heating and cooling categories, service areas, photos.", "Consistent business listings across the web.", "Fast mobile website with click-to-call."] },
      { when: "Days 31–60", title: "Seasonal pages", steps: ["Separate pages for furnace repair, AC repair, heat pumps, and replacements.", "Area pages for Monument, Falcon, and Fountain if you serve them.", reviews] },
      { when: "Days 61–90", title: "Year-round demand", steps: ["Maintenance plan page and follow-ups to fill slow months.", "Weather-timed ads for freezes and heat waves.", "Monthly check of calls and rankings."] },
    ],
    caseStudySlugs: ["hvac-colorado-springs"],
    related: [
      { label: "HVAC SEO in Colorado Springs", to: "/colorado-springs-hvac-seo" },
      { label: "HVAC web design in Colorado Springs", to: "/hvac-web-design-colorado-springs" },
      { label: "HVAC marketing in Colorado", to: "/hvac-marketing-colorado" },
      { label: "HVAC leads guide", to: "/learn/hvac-leads-colorado" },
    ],
    faqs: [
      { q: "When should Springs HVAC companies start winter marketing?", a: "August or September, so your pages and Google profile are ready before the first cold nights." },
      { q: "Do you work with HVAC companies outside Colorado Springs?", a: "Yes — Monument, Falcon, Fountain, Woodland Park, and the rest of the Pikes Peak area." },
    ],
  },
  {
    path: "/castle-rock-plumbing-marketing",
    trade: "Plumbing",
    city: "Castle Rock",
    metaTitle: "Castle Rock Plumbing Marketing | Local SEO Plan",
    metaDescription: "Emergency search visibility, Google Maps, and a 90-day local SEO plan for Castle Rock plumbers serving Douglas County.",
    intro: "Castle Rock plumbing calls are urgent and local. We help plumbers show up first for emergency and water heater searches across Castle Rock, Castle Pines, and Parker.",
    areas: ["The Meadows", "Crystal Valley", "Founders Village", "Castle Pines", "Parker", "Larkspur"],
    searches: ["emergency plumber Castle Rock", "water heater replacement", "burst pipe repair", "plumber near me"],
    plan: [
      { when: "Days 1–30", title: "Foundation", steps: ["Google Business Profile with honest hours, service areas, and photos.", "Consistent listings across the web.", "Phone number first on every page of a fast mobile site."] },
      { when: "Days 31–60", title: "Emergency & service pages", steps: ["Emergency, water heater, drain, and sewer pages.", "Frozen and burst pipe page ready before winter.", reviews] },
      { when: "Days 61–90", title: "Expand the area", steps: ["Area pages for Castle Pines and Parker if you serve them.", "Local Services Ads for pay-per-call visibility.", "Monthly check of calls and rankings."] },
    ],
    caseStudySlugs: ["plumbing-monument-co", "landscaping-castle-rock"],
    caseStudyNote: "We don't have a Castle Rock plumbing case study yet. Here is a nearby plumbing result and a Castle Rock home-service result.",
    related: [
      { label: "Plumbing SEO in Castle Rock", to: "/plumbing-seo-castle-rock" },
      { label: "Plumbing web design in Castle Rock", to: "/plumbing-web-design-castle-rock" },
      { label: "South Metro contractor marketing", to: "/south-metro-contractor-marketing" },
      { label: "Plumbing leads guide", to: "/learn/plumbing-leads-colorado" },
    ],
    faqs: [
      { q: "Should Castle Rock plumbers run Local Services Ads?", a: "Often yes — they show above other results and you pay per call. They work best with a strong Google profile." },
      { q: "Do I need a page for Parker and Castle Pines?", a: "Only if you really serve them and want more work there." },
    ],
  },
  {
    path: "/fort-collins-electrical-marketing",
    trade: "Electrical",
    city: "Fort Collins",
    metaTitle: "Fort Collins Electrician Marketing | Local SEO Plan",
    metaDescription: "Google Maps, panel and EV charger pages, and a 90-day local SEO plan for Fort Collins electricians.",
    intro: "Northern Colorado growth means panel upgrades, EV chargers, and new homes. We help Fort Collins electricians show up for the jobs they want most.",
    areas: ["Old Town", "Midtown", "Timnath", "Windsor", "Wellington", "Loveland"],
    searches: ["electrician Fort Collins", "panel upgrade", "EV charger installation", "whole home generator"],
    plan: [
      { when: "Days 1–30", title: "Foundation", steps: ["Google Business Profile with licensing, service areas, and photos.", "Consistent listings across the web.", "Fast mobile website with click-to-call."] },
      { when: "Days 31–60", title: "High-value job pages", steps: ["Panel upgrade, EV charger, generator, and lighting pages.", "Area pages for Windsor, Timnath, or Loveland if you serve them.", reviews] },
      { when: "Days 61–90", title: "Grow", steps: ["Targeted ads for upgrade searches.", "Track calls by source.", "Monthly check of calls and rankings."] },
    ],
    caseStudySlugs: ["electrical-pueblo", "cleaning-fort-collins"],
    caseStudyNote: "We don't have a Fort Collins electrical case study yet. Here is a Colorado electrical result and a Fort Collins home-service result.",
    related: [
      { label: "Electrical SEO in Fort Collins", to: "/electrical-seo-fort-collins" },
      { label: "Electrical web design in Fort Collins", to: "/electrical-web-design-fort-collins" },
      { label: "Electrical contractor marketing", to: "/electrical-contractor-marketing-colorado" },
      { label: "Electrician leads guide", to: "/learn/electrician-leads-colorado" },
    ],
    faqs: [
      { q: "What jobs should Fort Collins electricians target online?", a: "Panel upgrades, EV chargers, and generators are usually worth more than small repairs." },
      { q: "Do you serve Loveland and Windsor too?", a: "Yes, as part of a Fort Collins plan when you serve those towns." },
    ],
  },
];
